import { useRef, useSyncExternalStore } from 'react';

interface ITicker {
    now: number;
    subscribe: (listener: () => void) => () => void;
    read: () => number;
}

/** Один тикер на интервал на всё приложение: часы всех строк и карточек идут одним таймером и в одном такте. */
const tickers = new Map<number, ITicker>();

const createTicker = (interval: number): ITicker => {
    const listeners = new Set<() => void>();
    let timer: ReturnType<typeof setInterval> | undefined;
    // Пока тикер стоит, рендер берёт время не старше секунды (одно значение на все вызовы в рендере), а с первым
    // подписчиком часы идут от свежего Date.now(): часы пошли — не со времени прошлой остановки
    const freshness = Math.min(interval, 1000);
    const ticker: ITicker = {
        now: Date.now(),
        subscribe: (listener) => {
            const now = Date.now();
            const stale = Math.abs(now - ticker.now) >= freshness;
            if (timer === undefined) {
                ticker.now = now;
                timer = setInterval(() => {
                    ticker.now = Date.now();
                    listeners.forEach((notify) => notify());
                }, interval);
            } else if (stale) {
                // Тикер идёт, но последний такт был давно (длинный интервал, фоновая вкладка): новые часы не берут его
                // время, а идущие получают свежее вместе с ними — одно время на всех
                ticker.now = now;
                listeners.forEach((notify) => notify());
            }
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
                if (listeners.size > 0 || timer === undefined) return;
                clearInterval(timer);
                timer = undefined;
            };
        },
        read: () => {
            if (timer === undefined) {
                const now = Date.now();
                if (Math.abs(now - ticker.now) >= freshness) ticker.now = now;
            }
            return ticker.now;
        },
    };
    return ticker;
};

const tickerOf = (interval: number): ITicker => {
    let ticker = tickers.get(interval);
    if (!ticker) {
        ticker = createTicker(interval);
        tickers.set(interval, ticker);
    }
    return ticker;
};

const idle = () => () => undefined;

/**
 * Текущее время, обновляется раз в `interval` мс: часы ожидания, «на связи», длительность открытого сеанса идут сами.
 * `null` — не тикает (сеанс завершён): время то, что было при монтировании или последнем такте.
 */
export function useNow(interval: number | null = 1000): number {
    const ticker = interval === null ? undefined : tickerOf(interval);
    // время, на котором часы остановились: при монтировании или последний такт
    const stopped = useRef<number>(undefined);
    stopped.current ??= Date.now();
    const now = useSyncExternalStore(ticker?.subscribe ?? idle, ticker?.read ?? (() => stopped.current!));
    stopped.current = now;
    return now;
}
